<script setup>
import { computed, h, inject, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import Step from "@Components/Stepper/Step.vue";
import { useForm } from "@/form";
import pLimit from "p-limit";
import _chunk from "lodash/chunk";
import { useRoute } from "vue-router";
import GenerateTitlesForm from "./Partials/GenerateTitlesForm.vue";
import GenerateSiloingTitlesForm from "./Partials/GenerateSiloingTitlesForm.vue";
import GenerateKeywordsTitlesForm from "./Partials/GenerateKeywordsForm.vue";
import GenerateHaloscanTitlesForm from "./Partials/GenerateHaloscanForm.vue";
import NextButton from "@Components/Stepper/NextButton.vue";
import Loading from "@Icons/Loading.vue";

const props = defineProps(["modelValue", "step"]);
const emit = defineEmits(["update:modelValue", "next", "back", "skip"]);

const { t: __ } = useI18n();
const { params: routeParams } = useRoute();

const requestsSettings = inject("requestsSettings");

const processing = ref(false);

const LoadingIcon = h(
    "span",
    {
        class: "inline-flex items-center justify-center",
    },
    [h(Loading, { class: "w-4 h-4" })]
);

let style = undefined;
if (routeParams.style) {
    style = routeParams.style;
}

const generateTitlesForm = useForm({
    style: style,
    language: props.modelValue.language,
    keyword: "",
    length: 5,
    custom_instructions: "",
});

const generateHaloscanTitlesForm = useForm({
    type: "match",
    style: "informative",
    language: props.modelValue.language,
    keyword: "",
    length: 5,
    custom_instructions: "",
});

const generateSiloingTitlesForm = useForm({
    style: "informative",
    language: props.modelValue.language,
    keyword: "",
    custom_instructions: "",
});
const generateKeywordsTitlesForm = reactive({
    style: "informative",
    language: props.modelValue.language,
    keywords: "",
    custom_instructions: "",
});

const submitGenerateTitles = async () => {
    if (processing.value) {
        return;
    }

    processing.value = true;

    generateTitlesForm.language = props.modelValue.language;
    const response = await generateTitlesForm.post("titles_generate");

    props.modelValue.requests = response.map((i) => ({
        title: i,
        meta: {
            keyword: generateTitlesForm.keyword,
        },
    }));

    processing.value = false;

    emit("next");
};

const submitGenerateSiloingTitles = async () => {
    if (processing.value) {
        return;
    }

    processing.value = true;

    generateSiloingTitlesForm.language = props.modelValue.language;
    const response = await generateSiloingTitlesForm.post("titles_generate_siloing");
    props.modelValue.requests = response;

    processing.value = false;

    emit("next");
};

const submitGenerateHaloscanTitles = async () => {
    if (processing.value) {
        return;
    }

    processing.value = true;

    generateHaloscanTitlesForm.language = props.modelValue.language;
    const response = await generateHaloscanTitlesForm.post("titles_generate_haloscan");

    props.modelValue.requests = response.map((i) => ({
        title: i,
        meta: {
            keyword: generateHaloscanTitlesForm.keyword,
        },
    }));

    processing.value = false;

    emit("next");
};

const limit = pLimit(10);
const submitGenerateKeywordsTitles = async () => {
    if (processing.value) {
        return;
    }

    if (generateKeywordsTitlesForm.keywords.length === 0) {
        return;
    }
    let chunks = _chunk(generateKeywordsTitlesForm.keywords.split("\n"), 50);
    const promises = [];

    processing.value = true;

    chunks.forEach((chunk) => {
        promises.push(
            limit(async () => {
                const data = await useForm({
                    style: generateKeywordsTitlesForm.style,
                    language: props.modelValue.language,
                    keywords: chunk.join("\n"),
                    custom_instructions: generateKeywordsTitlesForm.custom_instructions,
                }).post("titles_generate_keywords");

                return data;
            })
        );
    });

    props.modelValue.requests = [];
    (await Promise.all(promises)).forEach((result) => {
        props.modelValue.requests = props.modelValue.requests.concat(result);
    });

    processing.value = false;

    emit("next");
};

const canGenerateTitle = computed(() => {
    if (processing.value) {
        return false;
    }

    switch (style) {
        case "siloing":
            if (generateSiloingTitlesForm.keyword.length === 0) {
                return false;
            }
            break;
        case "haloscan":
            if (generateHaloscanTitlesForm.keyword.length === 0) {
                return false;
            }
            break;
        case "keywords":
            if (generateKeywordsTitlesForm.keywords.length === 0) {
                return false;
            }
            break;
        default:
            if (generateTitlesForm.keyword.length === 0) {
                return false;
            }
            break;
    }

    return true;
});

const submit = () => {
    switch (style) {
        case "siloing":
            submitGenerateSiloingTitles();
            break;
        case "haloscan":
            submitGenerateHaloscanTitles();
            break;
        case "keywords":
            submitGenerateKeywordsTitles();
            break;
        default:
            submitGenerateTitles();
            break;
    }
};

if (style === "siloing") {
    requestsSettings.value.is_siloing = false;
}

const formLabels = {
    siloing: {
        title: __("Siloing"),
        description: __("Générer des pages en silo"),
        keywordLabel: __("Saississez un mot clé ou une expression"),
        keywordPlaceholder: __("Exemple : Voyage en Polynésie"),
        keywordHelp: __("Générez une architure de pages en silo depuis le mot clé ou expression saisie."),
        styleLabel: __("Style de génération de titre"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    haloscan: {
        title: __("Haloscan"),
        description: __("Générer des titres avec l'outil Haloscan"),
        keywordLabel: __("Saississez un mot clé, une expression ou un domaine"),
        lengthLabel: __("Nombre de titres à générer"),
        typeLabel: __("Type de génération de mots clés"),
        styleLabel: __("Style de génération de titre"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    keywords: {
        title: __("Ajouter une liste de mots clés"),
        description: __("Générer des titres sur vos mots clés"),
        keywordsLabel: __("Saisissez une liste de mots clés"),
        keywordsHelp: __("Un mot clé par ligne."),
        styleLabel: __("Style de génération de titre"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    commercial: {
        title: __("Commercial"),
        description: __("Générer des titres commerciaux"),
        examples: ["Acheter un appartement au meilleur prix – Offres exclusives & conseils d’experts !"],
        keywordLabel: __("Saississez un mot clé ou une expression"),
        keywordPlaceholder: __("Exemple : Acheter un appartement"),
        keywordHelp: __("Générez des titres en relation avec le mot clé ou expression saisie."),
        lengthLabel: __("Nombre de titres à générer"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    local: {
        title: __("Local"),
        description: __("Générer des titres localisés"),
        examples: ["Hôtel Le Brando, un resort de luxe au coeur de la Polynésie française"],
        keywordLabel: __("Saississez votre mot clé + géolocalisation"),
        keywordPlaceholder: __("Exemple : Hôtels + Polynésie"),
        keywordHelp: __("Générez des titres en relation avec le mot clé + géolocalisation saisie."),
        lengthLabel: __("Nombre de titres à générer"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    brands: {
        title: __("Marques"),
        description: __("Générer des titres sur des marques connues"),
        examples: ["Yoast SEO : Comment optimiser le SEO de votre site WordPress"],
        keywordLabel: __("Saississez une thématique"),
        keywordPlaceholder: __("Exemple : Plugin WordPress SEO"),
        keywordHelp: __("Générez des titres sur des marques connues en relation avec la thématique saisie."),
        lengthLabel: __("Nombre de titres à générer"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    comparative: {
        title: __("Comparatif"),
        description: __("Générer des titres comparatifs sur des marques ou des produits"),
        examples: ["Expedia vs Skyscanner : Quelle est la meilleure option pour réserver un voyage ?"],
        keywordLabel: __("Saississez une thématique"),
        keywordPlaceholder: __("Exemple : Compagnie aérienne"),
        keywordHelp: __("Générez des titres comparatifs en relation avec la thématique saisie."),
        lengthLabel: __("Nombre de titres à générer"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    guide: {
        title: __("Guide"),
        description: __("Générer des titres de guide"),
        examples: ["Sécuriser WordPress : Le guide complet pour proteger votre site WordPress"],
        keywordLabel: __("Saississez un mot clé ou une expression"),
        keywordPlaceholder: __("Exemple : WordPress"),
        keywordHelp: __("Générez des titres de guide en relation avec le mot clé ou expression saisie."),
        lengthLabel: __("Nombre de titres à générer"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
    informative: {
        title: __("Informatif"),
        description: __("Générer des titres informatifs"),
        examples: ["Quel est le prix d'une construction de maison en bois ?"],
        keywordLabel: __("Saississez un mot clé ou une expression"),
        keywordPlaceholder: __("Exemple : Construction de maison en bois"),
        keywordHelp: __("Générez des titres en relation avec le mot clé ou expression saisie."),
        lengthLabel: __("Nombre de titres à générer"),
        customInstructionsLabel: __("Instructions personnalisées"),
        customInstructionsHelp: __("Personnalisez les instructions de génération pour obtenir des titres plus adaptés à vos besoins spécifiques."),
    },
};
</script>

<template>
    <Step :step="step" @next="$emit('next')" @back="$emit('back')" @skip="$emit('skip')" class="px-4 lg:px-8 sm:px-6">
        <template #header>
            <div class="mx-auto max-w-3xl">
                <h3 class="text-4xl font-semibold leading-tight text-center text-gray-900 dark:text-white">
                    {{ formLabels[style].title }}
                </h3>
                <p class="mt-3 text-xl text-center text-gray-600 dark:text-gray-400">
                    {{ formLabels[style].description }}
                </p>
            </div>
        </template>

        <div class="mx-auto max-w-5xl flex flex-col space-y-16">
            <template v-if="style === 'siloing'">
                <GenerateSiloingTitlesForm
                    v-model="generateSiloingTitlesForm"
                    :title="formLabels.siloing.title"
                    :description="formLabels.siloing.description"
                    :keywordLabel="formLabels.siloing.keywordLabel"
                    :keywordPlaceholder="formLabels.siloing.keywordPlaceholder"
                    :keywordHelp="formLabels.siloing.keywordHelp"
                    :styleLabel="formLabels.siloing.styleLabel"
                    :customInstructionsLabel="formLabels.siloing.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.siloing.customInstructionsHelp" />
            </template>
            <template v-else-if="style === 'haloscan'">
                <generateHaloscanTitlesForm
                    v-model="generateHaloscanTitlesForm"
                    :title="formLabels.haloscan.title"
                    :description="formLabels.haloscan.description"
                    :keywordLabel="formLabels.haloscan.keywordLabel"
                    :lengthLabel="formLabels.haloscan.lengthLabel"
                    :typeLabel="formLabels.haloscan.typeLabel"
                    :styleLabel="formLabels.haloscan.styleLabel"
                    :customInstructionsLabel="formLabels.haloscan.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.haloscan.customInstructionsHelp" />
            </template>
            <template v-else-if="style === 'keywords'">
                <GenerateKeywordsTitlesForm
                    v-model="generateKeywordsTitlesForm"
                    :title="formLabels.keywords.title"
                    :description="formLabels.keywords.description"
                    :keywordsLabel="formLabels.keywords.keywordsLabel"
                    :keywordsHelp="formLabels.keywords.keywordsHelp"
                    :styleLabel="formLabels.keywords.styleLabel"
                    :customInstructionsLabel="formLabels.keywords.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.keywords.customInstructionsHelp" />
            </template>
            <template v-else-if="style === 'commercial'">
                <GenerateTitlesForm
                    ref="commercialModal"
                    @submit="submitGenerateTitles"
                    v-model="generateTitlesForm"
                    :title="formLabels.commercial.title"
                    :description="formLabels.commercial.description"
                    :examples="formLabels.commercial.examples"
                    :keywordLabel="formLabels.commercial.keywordLabel"
                    :keywordPlaceholder="formLabels.commercial.keywordPlaceholder"
                    :keywordHelp="formLabels.commercial.keywordHelp"
                    :lengthLabel="formLabels.commercial.lengthLabel"
                    :customInstructionsLabel="formLabels.commercial.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.commercial.customInstructionsHelp" />
            </template>
            <template v-else-if="style === 'local'">
                <GenerateTitlesForm
                    ref="localModal"
                    @submit="submitGenerateTitles"
                    v-model="generateTitlesForm"
                    :title="formLabels.local.title"
                    :description="formLabels.local.description"
                    :examples="formLabels.local.examples"
                    :keywordLabel="formLabels.local.keywordLabel"
                    :keywordPlaceholder="formLabels.local.keywordPlaceholder"
                    :keywordHelp="formLabels.local.keywordHelp"
                    :lengthLabel="formLabels.local.lengthLabel"
                    :customInstructionsLabel="formLabels.local.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.local.customInstructionsHelp" />
            </template>
            <template v-else-if="style === 'brands'">
                <GenerateTitlesForm
                    ref="brandsModal"
                    @submit="submitGenerateTitles"
                    v-model="generateTitlesForm"
                    :title="formLabels.brands.title"
                    :description="formLabels.brands.description"
                    :examples="formLabels.brands.examples"
                    :keywordLabel="formLabels.brands.keywordLabel"
                    :keywordPlaceholder="formLabels.brands.keywordPlaceholder"
                    :keywordHelp="formLabels.brands.keywordHelp"
                    :lengthLabel="formLabels.brands.lengthLabel"
                    :customInstructionsLabel="formLabels.brands.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.brands.customInstructionsHelp" />
            </template>
            <template v-else-if="style === 'comparative'">
                <GenerateTitlesForm
                    ref="comparativeModal"
                    @submit="submitGenerateTitles"
                    v-model="generateTitlesForm"
                    :title="formLabels.comparative.title"
                    :description="formLabels.comparative.description"
                    :examples="formLabels.comparative.examples"
                    :keywordLabel="formLabels.comparative.keywordLabel"
                    :keywordPlaceholder="formLabels.comparative.keywordPlaceholder"
                    :keywordHelp="formLabels.comparative.keywordHelp"
                    :lengthLabel="formLabels.comparative.lengthLabel"
                    :customInstructionsLabel="formLabels.comparative.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.comparative.customInstructionsHelp" />
            </template>
            <template v-else-if="style === 'guide'">
                <GenerateTitlesForm
                    ref="guideModal"
                    @submit="submitGenerateTitles"
                    v-model="generateTitlesForm"
                    :title="formLabels.guide.title"
                    :description="formLabels.guide.description"
                    :examples="formLabels.guide.examples"
                    :keywordLabel="formLabels.guide.keywordLabel"
                    :keywordPlaceholder="formLabels.guide.keywordPlaceholder"
                    :keywordHelp="formLabels.guide.keywordHelp"
                    :lengthLabel="formLabels.guide.lengthLabel"
                    :customInstructionsLabel="formLabels.guide.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.guide.customInstructionsHelp" />
            </template>
            <template v-else>
                <GenerateTitlesForm
                    ref="informativeModal"
                    @submit="submitGenerateTitles"
                    v-model="generateTitlesForm"
                    :title="formLabels.informative.title"
                    :description="formLabels.informative.description"
                    :examples="formLabels.informative.examples"
                    :keywordLabel="formLabels.informative.keywordLabel"
                    :keywordPlaceholder="formLabels.informative.keywordPlaceholder"
                    :keywordHelp="formLabels.informative.keywordHelp"
                    :lengthLabel="formLabels.informative.lengthLabel"
                    :customInstructionsLabel="formLabels.informative.customInstructionsLabel"
                    :customInstructionsHelp="formLabels.informative.customInstructionsHelp" />
            </template>
        </div>

        <template #footer>
            <div class="flex flex-col justify-center items-center py-6 space-y-3">
                <NextButton @click="submit" :disabled="!canGenerateTitle" :label="__('Générer vos titres')" :icon="processing ? LoadingIcon : undefined" />
            </div>
        </template>
    </Step>
</template>
